/**
 * v-safe-html 디렉티브 — v-html 대체용 전역 sanitizing 디렉티브.
 *
 * 바인딩 값을 DOMPurify 로 정화한 뒤 innerHTML 에 주입한다.
 * <script>, <iframe>, on* 핸들러, javascript: URI 등 XSS 벡터는 DOMPurify 기본값으로 차단되고,
 * 아래 allowlist 의 서식 태그(b, i, u, br, p, div, 목록, 헤딩 등)만 남는다.
 *
 * 사용: <p v-safe-html="content"></p>   (기존 v-html / sanitizeHtml() 호출을 대체)
 *
 * 참고: data-* 속성은 DOMPurify 의 ALLOW_DATA_ATTR 기본값(true)으로 유지된다
 *       (PDF 패널의 blank-line data-key 등 레이아웃 로직 보존).
 */
import DOMPurify from 'dompurify';

// 서식 태그 + 워드형 본문 요소(figure/figcaption/hr) + 제한적 iframe(YouTube/Vimeo 임베드).
const ALLOWED_TAGS = ['br', 'p', 'b', 'i', 'u', 'strong', 'em', 'span', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'a', 'img', 'figure', 'figcaption', 'hr', 'iframe'];
const ALLOWED_ATTR = ['class', 'style', 'href', 'target', 'rel', 'src', 'alt', 'title', 'width', 'height', 'allow', 'allowfullscreen', 'frameborder'];

// iframe 은 task 006 에서 막은 XSS 벡터 → 신뢰 호스트(YouTube/Vimeo 임베드)만 허용, 나머지는 제거.
const ALLOWED_EMBED_HOSTS = [
  'www.youtube.com', 'youtube.com', 'www.youtube-nocookie.com', 'youtube-nocookie.com', 'player.vimeo.com',
];
function isAllowedEmbed(src) {
  try {
    const u = new URL(src, window.location.origin);
    return u.protocol === 'https:' && ALLOWED_EMBED_HOSTS.includes(u.hostname);
  } catch {
    return false;
  }
}

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  // target="_blank" 링크에 rel="noopener noreferrer" 강제 (역 탭내빙 방지).
  if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer');
  }
  // iframe: 신뢰 임베드 호스트가 아니면 노드 제거 (임의 iframe = 클릭재킹/XSS 차단).
  if (node.tagName === 'IFRAME') {
    if (isAllowedEmbed(node.getAttribute('src') || '')) {
      node.setAttribute('allowfullscreen', '');
    } else {
      node.remove();
    }
  }
});

function render(el, binding) {
  el.innerHTML = DOMPurify.sanitize(binding.value == null ? '' : String(binding.value), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  });
}

export const safeHtml = {
  mounted: render,
  updated(el, binding) {
    // 바인딩 값이 바뀐 경우에만 재정화 — 매 리렌더마다 불필요한 DOMPurify/innerHTML 재설정 방지
    if (binding.value === binding.oldValue) return;
    render(el, binding);
  },
};

export default safeHtml;
